/**
 * Twin API service — client for Digital Twin endpoints.
 */
import type {
  TwinEvent,
  Pruefstatus,
  PruefstatusArea,
  EntityEvent,
} from '@hofsync/design-system';
import api from './api';
import { fetchAllPages } from './paging';

/**
 * One row of the source-system registry (#3385) — the wire shape of
 * ``GET /v1/twin/provenance/source-systems``, itself
 * ``config/source-systems.yaml``.
 *
 * ``token_prefixes`` is optional and defaults to ``[id]``: it exists because
 * one writer's raw ``source_id`` family does not always equal its canonical
 * id (the bank backfill stamps ``bank-backfill:<institution>:…`` under the id
 * ``bank``).
 */
export interface SourceSystemEntry {
  id: string;
  display_name: string;
  token_prefixes?: readonly string[];
}

/** What the SPA sends to create a link; the backend fills the rest. */
export interface DocumentLinkCreate {
  document_id: string;
  target_type: string;
  target_id: string;
  link_type: string;
  /** `line` + the row index for `price`/`quantity` (FR-017 #5); the server stamps the row's fingerprint. */
  anchor_kind?: 'line';
  anchor_ref?: string;
  payload?: Record<string, unknown>;
}

/** FR-017 #7a — `GET /v1/twin/links/census`; the SPA reads `unrouted.count`. */
export interface LinkCensus {
  coverage: {
    live_links: number;
    by_type: Record<string, { live: number; by_author: Record<string, number> }>;
    documents_with_links: number;
    linked_targets: number;
  };
  unrouted: { count: number; eligible_documents: number };
  conflicts: Record<string, { count: number; items: unknown[] }>;
}

/**
 * FR-017 — one typed relationship, as `GET /v1/twin/documents/{id}/links`
 * returns it. The keys are the link table's (`target_type`/`target_id`), not
 * the entity API's `entity_*`: the first version of this interface guessed the
 * latter, its test fixtures shared the guess, and every target rendered as
 * "undefined · undefined" until a real payload was read.
 */
export interface LinkTargetSummary {
  reference?: string;
  counterparty?: string;
  /** ISO day. */
  date?: string;
  /** Signed: a cash flow's outflow is negative. */
  amount?: number;
  currency?: string;
}

export interface DocumentLink {
  link_id: string;
  document_id: string;
  target_kind: string;
  target_type: string;
  target_id: string;
  /** Display name resolved by the backend; `null` when it could not be. */
  target_name?: string | null;
  /**
   * What a nameless target is recognised by (an invoice's number, seller, date
   * and amount), read from the same row as the name; `null` when the row could
   * not be read. Only the keys found are present.
   */
  target_summary?: LinkTargetSummary | null;
  link_type: string;
  anchor_kind: string | null;
  anchor_ref: string | null;
  created_by: string;
  payload?: Record<string, unknown>;
  link_state?: string;
  target_state?: string;
}

// Source document linked to an entity via facts
export interface EntitySourceDoc {
  document_id: string;
  doc_type: string | null;
  filename: string | null;
  document_date: string | null;
  summary: Record<string, string>;
  /**
   * FR-017 link types this document asserts about the entity — already
   * filtered (no `generic`) and sorted by the backend.
   *
   * Optional because a backend older than FR-017 #3 does not send the key
   * across the rolling-deploy window; the panel treats absent and empty
   * alike.
   */
  link_types?: string[];
}

// Response types
export interface EventListResponse {
  events: TwinEvent[];
  total: number;
  page: number;
  limit: number;
}

export interface EntityListResponse {
  entities: Record<string, unknown>[];
  total: number;
  primary_key: string | null;
  // Issue #593 (2026-05-02): pagination fields. Backend's
  // ``twin_data_service.list_entities`` already returns these, but they
  // were not declared in the FE type — so callers couldn't read them
  // without an unsafe cast. ``page`` is 1-indexed (matches the
  // backend's ``Query(1, ge=1)`` default); ``limit`` is the requested
  // page size or ``null`` when the caller didn't paginate; ``has_more``
  // is true when ``page * limit < total``. All three are optional so
  // legacy callers that ignore pagination keep type-checking.
  page?: number;
  limit?: number | null;
  has_more?: boolean;
}

export interface DerivedFact {
  derived_fact_id: string;
  entity_type: string;
  entity_id: string;
  fact_type: string;
  fact_value: Record<string, unknown>;
  rule_id: string | null;
  computed_at: string;
}

export interface DerivedFactListResponse {
  derived_facts: DerivedFact[];
  total: number;
  page: number;
  limit: number | null;
  has_more: boolean;
}

/**
 * How complete the server says a Preisspiegel read was (#2646).
 *
 * The Preisspiegel banner used to derive this from the FE's own page-walk cap,
 * which could only ever report the one failure it caused itself. These flags
 * come from the aggregate: ``truncated`` = the cell cap fired, ``degraded`` =
 * part of the population could not be read, ``malformed`` = rows whose payload
 * would not parse. ``cell_count`` is what came back.
 */
export interface PreisspiegelDataQuality {
  truncated: boolean;
  degraded: boolean;
  malformed: number;
  cell_count: number;
  /**
   * HOF-19 — the PLAUSIBILITY MARKS could not be computed for this answer, so
   * every cell here is unmarked because it was unchecked, not because it passed.
   *
   * Deliberately its own flag rather than part of ``degraded``: no cell is
   * missing, and ``degraded``/``truncated`` are what the #2652 bridge gate
   * suppresses on. But the client MUST read it, because "no marks" is exactly
   * what a healthy in-band tenant looks like — an unread flag would leave a
   * failed check and a clean chart rendering identically, which is the whole
   * ambiguity the flag exists to close.
   *
   * Optional: a server that predates the mark sends nothing, and absent reads as
   * "the marks are fine" — the same direction every other additive key here
   * takes, and the right one, because a pre-HOF-19 server computed no marks that
   * could have failed.
   */
  marks_unavailable?: boolean;
  /**
   * HOF-19 Phase B — the AMI BENCHMARK read failed or was truncated.
   *
   * Its own pair of flags for the same reason as ``marks_unavailable``: a
   * benchmark that could not load costs no booked cell, so folding it into
   * ``degraded`` would suppress the #2652 off-season bridges over a complete
   * price history — a second, unrelated regression bought with a fix.
   *
   * Optional, and absent reads as "fine": a server that predates the benchmark
   * sends nothing and had no benchmark read that could have failed.
   */
  benchmark_degraded?: boolean;
  benchmark_truncated?: boolean;
  consumer_benchmark_degraded?: boolean;
  consumer_benchmark_truncated?: boolean;
  /**
   * HOF-248 — the same pair for the PUBLIC benchmark population, which the
   * server reads on its own leg against the shared benchmark tenant.
   *
   * A separate pair rather than an OR, because the sentence the chart renders
   * NAMES the source: the two populations are different measurement kinds, and
   * a tenant with no AMI licence must not be told AMI failed. Optional and
   * absent-reads-as-fine, exactly like the pair above.
   */
  trade_benchmark_degraded?: boolean;
  trade_benchmark_truncated?: boolean;
  benchmark_levels?: string[];
}

/**
 * One ``(catalog_item_id, side)`` row of ``GET /v1/twin/preisspiegel/index``
 * (#2694) — the whole answer the page SHELL needs: does this item have data,
 * and as of when.
 *
 * The keys are flattened out of the cell's ``keys`` map by the backend, so no
 * caller has to know UDB's positional group-key grammar to read an item id.
 *
 * **There is deliberately no price here.** The collapsed row does hold
 * ``mean``/``volume_weighted_mean``/``last`` and relaying them would be one
 * line, but at this grain they span every counterparty, quality and channel at
 * once — the single cross-counterparty number #2388 forbids. Tile and chart
 * prices come from ``/cells`` or ``/series``, per counterparty.
 *
 * ``catalog_item_id`` is nullable: an observation the crosswalk could not
 * attribute is RELAYED rather than filtered, and UDB's group-key order is
 * NULLS FIRST, so such a row sorts first.
 */
export interface PriceIndexRow {
  catalog_item_id: string | null;
  side: string | null;
  /**
   * The unit this group's facts were PUBLISHED under (#2944) — ``fact_value.unit``,
   * stamped at write time, NOT the unit the catalog declares for the item today.
   *
   * This route is where the unit reaches the page: the catalog-wide ``/cells``
   * read cannot carry it (``CATALOG_CELL_GROUP_BY`` is at UDB's six-key group
   * ceiling), so its cells arrive unlabelled and the tile is labelled from here.
   *
   * It is part of the GROUP KEY, so an item whose facts span two units arrives as
   * two rows — the signal that the tile cannot be given one honest label. See
   * ``data_quality.mixed_unit_items``.
   *
   * ``null`` means the facts state no unit. That is an absence to report, never
   * an invitation to substitute the catalog's — doing so is the bug this field
   * exists to close.
   */
  unit: string | null;
  /** Rows (invoice lines) behind this ``(item, side, unit)`` group. */
  n: number;
  first_at: string | null;
  /**
   * The group's latest observed day — the tile's *Stand*. NULL despite ``n > 0``
   * means "no readable date in this group", NOT "fresh": see
   * ``dateless_groups``.
   */
  last_at: string | null;
  /**
   * Curated price-book ESTIMATES in this group (#2777) — reference prices from
   * ``custom_seed_prices``, never trades.
   *
   * ``n`` above became BOOKED-only in 2777c, and this is where the population it
   * stopped counting went. The pairing is load-bearing in both directions: a
   * client that reads ``n`` alone deletes every estimate-only item from the page
   * (the seven #2694 Saatgut tiles), and a client that adds the two together
   * quotes a book price as a trade (#2695). Read them as "does it have anything
   * to show" = ``n > 0 || estimate_n > 0``, and "is what it shows booked" =
   * ``n > 0``.
   *
   * Always present — the route emits both keys on every entry, so no caller has
   * to tell "no estimates" from "old server".
   */
  estimate_n: number;
  /**
   * The estimate slice's own latest day. In practice the pinned book anchor
   * (``CUSTOM_PRICE_DATE`` = 2023-01-01, held against the Deckungsbeitrag deep
   * link #2538) — an anchor, not an observation, so it is a *Stand* only for an
   * item with no booked trades at all.
   */
  estimate_last_at: string | null;
}

/**
 * ``/index``'s data quality — the ``/cells`` contract plus the one failure mode
 * this grain introduces.
 *
 * ``dateless_groups`` counts groups with observations but no ``last_at``. UDB
 * builds ``at_last`` as ``ARRAY_AGG(obs_at ORDER BY … DESC)[1]`` and Postgres
 * ``DESC`` implies NULLS FIRST, so ONE row with an unreadable date nulls
 * ``last_at`` for its WHOLE ``(item, side)`` group. Under the day bucket that
 * row is quarantined in its own ``bucket: null`` cell; collapsing to
 * ``bucket="none"`` is what lets it contaminate the item. Treat it as "no
 * *Stand*", never as fresh data. The route also raises ``degraded`` when it
 * fires.
 */
export interface PreisspiegelIndexDataQuality extends PreisspiegelDataQuality {
  /**
   * ``degraded`` restricted to its READ cause (#2944) — the population could not
   * be fully read, as opposed to being read completely and found unusable.
   *
   * This route raises ``degraded`` for three different things: a failed/partial
   * read, ``dateless_groups``, and ``mixed_unit_items``. The last two are
   * COMPLETE reads, and the client says something quite different about them
   * ("these rows cannot be labelled") than about the first ("whole Kategorien
   * may be missing"). The boolean alone cannot tell them apart, and subtracting
   * the counters to guess is unsound both ways — a real degradation alongside a
   * dateless group would be explained away. So the server states it.
   */
  read_degraded: boolean;
  dateless_groups: number;
  /**
   * Catalog items whose live facts were published under MORE THAN ONE unit
   * (#2944) — the state a tile's unit change leaves behind while old and new
   * facts coexist.
   *
   * Such a tile is rendered WITHOUT a unit: one headline is one number and one
   * Y-axis is one axis, and picking a winner between two units is how the wrong
   * one wins silently. This count is what lets the page explain the gap instead
   * of leaving it mysterious. The route also raises ``degraded`` when it fires.
   */
  mixed_unit_items: number;
}

/**
 * ``GET /v1/twin/preisspiegel/index``.
 *
 * Failure posture: **HTTP 200 with an empty ``index`` and
 * ``degraded``/``truncated`` set** — never a 500. The shell renders on this
 * answer, so a caller that reads an empty ``index`` without checking those
 * flags will render "no data" over a backend hiccup.
 */
export interface PreisspiegelIndexResponse {
  index: PriceIndexRow[];
  /**
   * HOF-19 Phase B / AC 22 — one row per item that has AMI benchmark data.
   *
   * A SIBLING surface, not more fields on `index`: those rows are
   * `(item, side, unit)` grain, and a shelf price has no side and one constant
   * unit — folding a benchmark count in would credit it to a fabricated grain.
   * The has-data fold ORs this in, which is what keeps a benchmark-only item
   * on the page for the tenant that licensed the panel. Optional: a server
   * that predates it sends nothing, and absent reads as "no benchmark".
   */
  benchmark_index?: { catalog_item_id: string | null; n: number; first_at: string | null; last_at: string | null }[];
  /** The key paths the rows are grouped by, in order. */
  group_by: string[];
  side: string | null;
  beispieldaten: boolean;
  data_quality: PreisspiegelIndexDataQuality;
}

/**
 * One ``(day × series)`` aggregate cell (#2646), as
 * ``GET /v1/twin/preisspiegel/cells`` returns it — UDB's
 * ``aggregate_derived_facts`` item shape, relayed verbatim.
 *
 * The route deliberately does not project this down: the per-item ``/series``
 * route renders through ``ObservedPoint.as_dict()`` (``{date, value}``) and so
 * drops ``n``, ``min``, ``max``, ``total_quantity``, ``total_amount``,
 * ``weighted``, ``first``/``last`` and ``first_at``/``last_at`` — which is
 * every field a day panel, a traded weight and a candle are made of.
 *
 * Reshaped into ``PriceSeries`` by ``pages/apps/preisspiegel/cells.ts``, which
 * documents what each field is load-bearing for.
 */
export interface PriceCell {
  /** The bucket — an ISO ``YYYY-MM-DD`` day. */
  bucket: string | null;
  /** Group key values, keyed by the path the server grouped on (see ``group_by``). */
  keys: Record<string, string | null>;
  /** Rows (invoice lines) behind this cell. */
  n: number;
  min: number | null;
  max: number | null;
  /** Unweighted arithmetic mean — present for comparison; NOT what is plotted. */
  mean: number | null;
  /** Σ money ÷ Σ catalog units — the price actually realised. Plotted. */
  volume_weighted_mean: number | null;
  /** Σ traded weight in catalog units (kg), or null when the cell is unweighted. */
  total_quantity: number | null;
  /** Σ line amounts in €, or null when the cell is unweighted. */
  total_amount: number | null;
  /** Whether a usable weight was found at all. */
  weighted: boolean;
  /** Rows carrying a value but no usable weight. */
  weight_missing: number;
  /** Rows with no usable value. */
  value_missing: number;
  first: number | null;
  last: number | null;
  first_at: string | null;
  last_at: string | null;
  /**
   * HOF-19 — the read-path plausibility verdict, present only when this cell's
   * €/kg breaches its crop's declared band.
   *
   * Snake_case because this object is UDB's item shape relayed verbatim and the
   * server writes it in the wire's own casing; ``cells.ts`` is where it becomes
   * the camelCase ``OutOfBandMark`` the rest of the app speaks.
   *
   * Optional, and ABSENT rather than false when there is nothing to say — so a
   * server that predates the mark and an in-band cell are the same, harmless
   * thing, and no consumer changes behaviour by not reading it.
   */
  out_of_band?: {
    crop: string;
    unit: string;
    value: number;
    published_unit: string;
    band_min: number;
    band_max: number;
    direction: 'high' | 'low';
  };
  /**
   * HOF-250 — WHICH statistic produced this cell's value, on the benchmark
   * read only (`chained_index` | `weighted_mean`).
   *
   * Per POINT, not per level, because a chained level is not chained
   * everywhere: a week whose cells share no retailer with the week before it
   * forms zero relatives, so the chain is severed there and the server
   * re-anchors on that week's own observation-weighted mean (see
   * `benchmark_index.weekly_chained_index`). Both statistics therefore appear
   * on one consumer line, and only the mean ones may wear an `Ø`.
   *
   * Optional and ABSENT rather than defaulted, so a server that predates it
   * degrades to the level's declared method rather than to a wrong label.
   */
  statistic?: string;
  /**
   * HOF-250 — this benchmark cell is where the chain was SEVERED and re-anchored.
   *
   * Present only on the first point of a re-anchored segment, and only on a
   * chained series: the two sides are two anchors, so the step across them is
   * not a price movement and the chart must break its line here rather than draw
   * through it. Absent — never `false` — everywhere else, so a series that has no
   * chain to break says nothing rather than claiming it was checked.
   */
  chain_break?: boolean;
}

/**
 * One source LINE behind a cell (HOF-19 AC 10/11), as
 * ``GET /v1/twin/preisspiegel/lines`` returns it.
 *
 * Fetched only when a reader drills into one cell. The bulk read stays at cell
 * grain deliberately — a build that pages every line for the drawn window is the
 * ~10.000-row ceiling #2646 removed, which dropped about half of WF's Erdbeeren
 * history behind a chart that looked complete.
 */
export interface PriceLine {
  date: string | null;
  value: number | null;
  unit: string | null;
  quantity: number | null;
  counterparty: string | null;
  quality: string | null;
  channel: string | null;
  /** The document this line was extracted from — AC 11, the link that makes a mark actionable. */
  source_doc_id: string | null;
  line_id: string | null;
  product_name: string | null;
  amount: number | null;
  source_unit_price: number | null;
  out_of_band?: PriceCell['out_of_band'];
}

/** One retail observation behind a benchmark cell (HOF-19 Phase B). */
export interface BenchmarkLine {
  date: string | null;
  value: number | null;
  unit: string | null;
  quality: string | null;
  retailer: string | null;
  city: string | null;
  pack_g: string | null;
  packaging: string | null;
  brand: string | null;
  origin: string | null;
  promo: string | null;
  /**
   * The benchmark's AC 11: the file this number was imported from — the AMI
   * workbook, or the public collector's CSV.
   */
  workbook: string | null;
  spot_id: string | null;
  /**
   * WHICH producer measured it. One constant for AMI; the public population
   * spans several collectors (`public.rewe_shop`, `public.kaufland_offers`, …)
   * and a reader weighing a shelf price against an offer price needs to know
   * which they are looking at. Optional — a server that predates it sends none.
   */
  source?: string | null;
  /**
   * The page the collector actually read, for a public row. Absent on AMI rows,
   * whose trace is the workbook.
   */
  provenance_url?: string | null;
  /**
   * HOF-544 — set only on the Shared Buy Index's aggregated row: the price level
   * (`grosshandel` / `einzelhandel`) whose deal index level it carries. Such a
   * row stands for many deals and names none of them.
   */
  price_level?: string | null;
  /** HOF-544 — the row is an aggregate, not an observation. */
  aggregated?: boolean;
}

/** ``GET /v1/twin/preisspiegel/benchmark-lines``. */
export interface PreisspiegelBenchmarkLinesResponse {
  item_id: string;
  series_id: string;
  bucket: string;
  date: string;
  lines: BenchmarkLine[];
  data_quality: {
    truncated: boolean;
    degraded: boolean;
    line_count: number;
    /**
     * HOF-544 — under a TRADE point only: the Shared Buy Index read failed or came
     * back incomplete, so the list shows the Großmarkt rows WITHOUT the deal level
     * the point was folded from. Absent on every other drill-down.
     */
    deals_degraded?: boolean;
  };
}

/** ``GET /v1/twin/preisspiegel/lines``. */
export interface PreisspiegelLinesResponse {
  item_id: string;
  series_id: string;
  bucket: string;
  date: string;
  lines: PriceLine[];
  data_quality: {
    truncated: boolean;
    degraded: boolean;
    /** Booked lines RETURNED — what the panel lists. */
    line_count: number;
    /** Every row the read returned, booked and price-book alike. */
    rows_read?: number;
    /**
     * Every row the twin holds for this cell, in the same UNFILTERED population
     * as {@link rows_read}. Never rendered beside {@link line_count}: those two
     * count different populations, and a reader shown "1 von 620" sees a
     * discrepancy they cannot explain.
     */
    total_rows?: number;
  };
}

/** ``GET /v1/twin/preisspiegel/cells``. */
/**
 * ``GET /v1/twin/preisspiegel/tiles`` — the grid in ONE request (HOF-271).
 *
 * The grid used to cost two round trips that could not overlap: ``/index``
 * answered "which items are on this page", and ``/cells`` — scoped to what
 * ``/index`` had just said (#2694) — answered "what do they show". On WF that was
 * ~5,5 s to tiles at the prod median and ~12 s at p90.
 *
 * The dependency was removable because the cells leg never actually needed the
 * INDEX's answer: it needed the open Kategorie's items, which is a catalog fact.
 * The backend reads the same ``config/catalog/preisspiegel@v1.yaml`` the frontend
 * compiles (#2771), so both legs now ride one ``asyncio.gather``.
 *
 * **The two halves are nested, not merged.** Each carries its own
 * ``data_quality``, and the two answer different questions — "whole Kategorien
 * may be missing from this page" versus "these cells are incomplete" — which the
 * page says quite different things about. Flattening them would collide
 * ``truncated``/``degraded``/``cell_count`` and hand one read's flags to the
 * other's numbers.
 */
export interface PreisspiegelTilesResponse {
  /**
   * The Kategorie the cell half covers.
   *
   * Echoed rather than assumed: on a cold visit (no ``?kat=``, no stored
   * preference) the client sends none and the SERVER applies the page's own
   * first-enabled rule (#2420/HOF-101) to the index it just computed. ``null``
   * for a tenant with no data at all.
   */
  kat: string | null;
  /** The item ids the cell half was read for — the Kategorie's, or an explicit scope. */
  items: string[];
  /** The ``/index`` body, verbatim. Always CATALOG-WIDE: the lens is a whole-tree question. */
  index: PreisspiegelIndexResponse;
  /** The ``/cells`` body, verbatim, scoped to {@link items}. */
  cells: PreisspiegelCellsResponse;
  /**
   * HOF-270 — the MATERIALISED per-item tile summaries the grid draws from.
   *
   * Optional on the wire so a client can outlive a backend that predates the
   * field: absent reads as "nothing is materialised", which is exactly the
   * fallback the server would have declared itself.
   */
  summaries?: PreisspiegelTileSummaries;
  /**
   * HOF-299 S3 (2026-09-04) — the tenant's DERIVED leaf tiles: its own `product`
   * entities, one tile per exact article / work, typed into a `leaves: derived`
   * Kategorie of the catalog by the server-side type rules. The whole tenant roster,
   * not only the open Kategorie's — the lens counts every Kategorie, and `findItem`
   * consults this list before the static catalog. Optional on the wire for the same
   * rollout reason as `summaries`: absent reads as "no derived tiles".
   */
  derived_tiles?: PreisspiegelDerivedTiles;
}

/**
 * One derived leaf tile on the wire (HOF-299 S3) — `TrackedItem`-shaped so the grid,
 * the chart and `findItem` take it as they take a static item. `id` is the `product`
 * entity id and the `catalog_item_id` its facts carry; `kategorie` the derived L2 the
 * type rule assigned; `unit` the product's OWN price unit (a catalog unit id).
 */
export interface PreisspiegelDerivedTile {
  id: string;
  name: string;
  unit: string;
  sides: string[];
  kategorie: string;
  basis?: string;
  derived: true;
}

export interface PreisspiegelDerivedTiles {
  items: PreisspiegelDerivedTile[];
  data_quality: {
    /** The roster read hit its bound — tiles are MISSING from the page, loudly. */
    truncated: boolean;
    /** The roster read failed — served as "no derived tiles" plus this flag. */
    degraded: boolean;
    /** Products the server could not serve as tiles (no unit, unknown Kategorie). */
    unservable: number;
    count: number;
  };
}

/**
 * The summary half of the grid response (HOF-270).
 *
 * The grid used to be drawn by aggregating the tenant's whole trading history in
 * the browser — 39.863 facts on WF behind four numbers and a shape. These rows
 * carry the aggregated INPUTS of each tile, one per item, so the page draws from
 * a few dozen rows whose count is bounded by the CATALOG rather than by how much
 * the farm has ever sold.
 */
/** One point of the materialised combined day series. */
export interface TileSummaryPoint {
  date: string;
  price: number;
}

/**
 * The ``preisspiegel_tile`` ``fact_value``, as written by UDB's set-based pass.
 *
 * Field-for-field the aggregated inputs of one tile. ``side`` is the population the
 * summary speaks for — ``null`` is the unscoped one the grid reads.
 *
 * **No counterparty name and no per-counterparty price appear anywhere in it.**
 * ``nSources`` is a count (#2539), which is what makes the fact carry the farm's own
 * cross-counterparty blend and nothing republishable (#2388, Antonio 2026-08-23).
 */
export interface PreisspiegelTileSummary {
  catalog_item_id: string;
  side: string | null;
  unit: string | null;
  unit_mixed: boolean;
  last_price: number | null;
  last_at: string | null;
  n: number;
  n_sources: number;
  estimated: boolean;
  change_pct: number | null;
  points: TileSummaryPoint[];
  /**
   * Optional since HOF-377, and OPTIONAL rather than fabricated.
   *
   * `/tiles/headlines` strips these from the wire — each is either read only on the
   * server (`first_at`, `estimate_n` are on the `index` block of the same response;
   * `yoy_baseline` is the input to `change_pct`) or is a property of the curve
   * (`window_days`, which travels with the points on `/tiles/histories`). Nothing in
   * this app reads any of them except `window_days`, which `tileModelFromSummary`
   * already defaults. Marking them optional says that; filling them with `0` / `''`
   * to satisfy the type would put a value in the payload that means nothing.
   */
  first_at?: string | null;
  estimate_n?: number;
  yoy_baseline?: { date: string; price: number } | null;
  window_days?: number;
  data_quality: {
    dateless: boolean;
    mixed_unit: boolean;
    complete: boolean;
  };
  /** Server-side only: the version gate and the freshness stamp. See above. */
  materialised_by?: string;
  computed_at?: string;
  /**
   * HOF-377 — the MATERIALISED benchmark presence bit, in the DRAWN vocabulary
   * (`consumer` / `trade`).
   *
   * The tile used exactly this one bit out of the whole benchmark gather, and the
   * grid paid 5-8 UDB aggregates of 2-5 s each for it on every visit. No tile ever
   * showed a benchmark VALUE. `benchmarkTileLabelFromLevels` turns it into the
   * scope line an own-empty, benchmark-backed tile carries.
   *
   * Optional on the wire so a bundle can outlive a `@v2` server: absent reads as
   * "no benchmark", which is what such a server would have said.
   */
  benchmark_levels?: string[];
}

/**
 * HOF-377 — one tile as `GET /twin/preisspiegel/tiles/headlines` returns it: the
 * summary WITHOUT its curve, and without the keys only the server reads.
 *
 * `points` is the whole reason the split exists — ~120 points per item is the bulk
 * of the payload and the thing first paint must not wait for — and it arrives on
 * {@link PreisspiegelTileHistory}. Everything else the tile draws is here.
 */
export type PreisspiegelTileHeadline = Omit<PreisspiegelTileSummary, 'points'> & { benchmark_levels: string[] };

/** One item's 120-day curve, from `GET /twin/preisspiegel/tiles/histories`. */
export interface PreisspiegelTileHistory {
  catalog_item_id: string;
  points: TileSummaryPoint[];
  window_days: number | null;
}

export interface PreisspiegelTileHistoriesResponse {
  kat: string | null;
  items: string[];
  histories: PreisspiegelTileHistory[];
  data_quality: { summaries_unavailable: boolean; truncated: boolean; degraded: boolean };
}

/**
 * The headline half of the grid response (HOF-377), in `/tiles`' own outer shape.
 *
 * Deliberately the SAME envelope: `index` is derived server-side from the very
 * rows the tiles come from, so `buildPriceIndexResult` is untouched and there is
 * no second derivation of the lens to drift; `cells` is the complete-and-empty
 * body, because this route reads no cells and that is not the same as a cell read
 * that failed.
 */
export interface PreisspiegelTileHeadlinesResponse {
  kat: string | null;
  items: string[];
  index: PreisspiegelIndexResponse;
  cells: PreisspiegelCellsResponse;
  summaries: {
    tiles: PreisspiegelTileHeadline[];
    /**
     * The WHOLE grid must be built the old way — the tenant-level fallback.
     *
     * There is no per-item one any more (spec Assumption 3): the writer now emits
     * a row for every item with prices OR benchmark coverage, so an item absent
     * from `tiles` has nothing to summarise by construction. What cannot be
     * inferred that way is "this tenant has prices and no drawable summaries at
     * all", which is a materialiser that has not run.
     */
    grid_fallback: boolean;
    data_quality: {
      summaries_unavailable: boolean;
      stale_count: number;
      freshness_unverified: boolean;
      truncated: boolean;
      degraded: boolean;
    };
  };
  derived_tiles?: PreisspiegelDerivedTiles;
}

export interface PreisspiegelTileSummaries {
  /** One row per covered item, in the response's own ``items`` order. */
  tiles: PreisspiegelTileSummary[];
  /**
   * The items with no summary that the INDEX vouches for — the ones the page must
   * build the old way, from {@link PreisspiegelTilesResponse.cells}.
   *
   * Named rather than inferred from an absence, because an inferred fallback
   * cannot tell "no summary because the item has no data" from "no summary
   * because the materialiser is broken", and only one of those is a defect.
   */
  fallback_items: string[];
  data_quality: {
    /** The summary read itself failed — an outage, not an unmaterialised tenant. */
    summaries_unavailable: boolean;
    truncated: boolean;
    fallback_count: number;
  };
}

export interface PreisspiegelCellsResponse {
  /** BOOKED trade observations only, since #2777. */
  cells: PriceCell[];
  /**
   * The CURATED PRICE BOOK in the same cell shape (#2777) — reference prices the
   * materialiser derived for crops with no trade behind them.
   *
   * A separate array rather than a flag on the cell, because the aggregate
   * response carries no provenance block at all: the two populations are
   * distinguishable only by which read returned them, and folding them into one
   * list would lose that for good. An un-updated client reading only ``cells``
   * therefore stops charting estimates as trades, which IS the fix — hidden is
   * the safe failure direction here, mislabelled is not.
   *
   * Optional so a client cannot crash on a server that predates the split; absent
   * reads as "no estimates".
   */
  estimate_cells?: PriceCell[];
  /**
   * HOF-19 Phase B — the AMI MarktMonitor retail panel, in a THIRD list.
   *
   * A separate array on exactly the reasoning ``estimate_cells`` uses, applied
   * one degree more strictly. These are not the farm's prices at all: they are
   * *Endverbraucherpreise* observed in shops, carrying a 15–60 % unknown Händler
   * markup, and they must never reach a statistic about what the farm sold (the
   * mengengewichteter Durchschnitt, a tile Ø, a day panel, a candle).
   *
   * The server enforces that by reading them from a different fact type, and the
   * client by keeping them in a different map — so a consumer cannot fold one in
   * by forgetting a flag, which is the failure mode a stamped-in-one-list design
   * leaves open.
   *
   * Optional: absent reads as "no benchmark", which renders the pre-HOF-19
   * chart. Hidden is the safe failure direction here; mislabelled is not.
   */
  benchmark_cells?: PriceCell[];
  /** The key paths the cells are grouped by, in order. */
  group_by: string[];
  /**
   * The benchmark's own grouping, which differs from ``group_by``: it has no
   * side, counterparty, quality or channel, because a shelf price in somebody
   * else's shop has none of those.
   */
  benchmark_group_by?: string[];
  side: string | null;
  beispieldaten: boolean;
  data_quality: PreisspiegelDataQuality;
}

/**
 * One live transaction ↔ twin-item link (#1624-D), as returned by
 * ``GET /twin/liquiditaet/links``. The reverse of a link write — projects the
 * ``liquiditaet_twin_link`` fact_value into a flat read shape. ``target_ref``
 * is the ``document_id`` (document target) or ``"{type}/{id}"`` (entity target).
 */
export interface LiquiditaetTwinLink {
  derived_fact_id: string;
  computed_at: string | null;
  transaction_id: string;
  target_kind: 'document' | 'entity';
  target_ref: string;
  target_document_id?: string | null;
  target_entity_type?: string | null;
  target_entity_id?: string | null;
  link_role?: string | null;
  note?: string | null;
  created_at?: string | null;
}

export interface LiquiditaetLinkListResponse {
  items: LiquiditaetTwinLink[];
  matched: number;
  mode: 'transaction' | 'twin_item';
}

/** Ack of a link / unlink write (``noop=true`` ⇒ unlink of an absent link). */
export interface LiquiditaetLinkResult {
  action: 'link' | 'unlink';
  derived_fact_id: string;
  transaction_id: string;
  target_kind: 'document' | 'entity';
  target_ref: string;
  fact_value: Record<string, unknown> | null;
  noop: boolean;
}

export interface TwinStats {
  active_contracts: number;
  fulfilled_pct: number;
  open_events_by_severity: {
    info: number;
    warning: number;
    action_required: number;
  };
  compliance_score: number;
}

export interface EventFilters {
  severity?: string;
  status?: string;
  event_type?: string;
  category?: string;
  page?: number;
  limit?: number;
}

export interface LenseChildConfig {
  id: string;
  name: string;
  default?: boolean;
  filter?: Record<string, string>;
  /**
   * Issue #669 — keys whose JSONB ``attributes`` value must be missing
   * (or empty string) on each row. Composes with ``filter`` via AND so
   * a child can use either or both. Surfaces "untyped tail" buckets
   * like Personen/Ohne Rolle and Organisationen/Ohne Typ.
   */
  filter_missing?: string[];
  /**
   * Fields a live crop cycle names: ``current`` = of this season („Aktuelle Flächen“), ``any`` = ever
   * („Alle Flächen“). Computed by the backend per request (the season rolls on 1 January), sent as
   * the reserved ``_cycle_season`` query parameter; composes with ``filter`` via AND.
   */
  filter_cycle_season?: 'current' | 'any';
}

export interface LenseFeedGroup {
  key: string;
  label: string;
}

export interface LenseItemIconEntry {
  icon: string;
  variant?: string;
}

export interface LenseItemIcons {
  key: string;
  composite?: boolean;
  map: Record<string, LenseItemIconEntry>;
}

export interface FeedDisplay {
  id_key?: string;
  group_key?: string;
  title: string;
  subtitle?: string;
  meta?: string;
}

export interface LenseFieldRef {
  entity: string;
  display: string;
}

export interface LenseField {
  id: string;
  name: string;
  type: string;
  kind: 'static' | 'derived';
  required?: boolean;
  panel?: boolean;
  derived_from?: string;
  ref?: LenseFieldRef;
  /**
   * Semantic meaning of the field — German prose sourced from the
   * ontology's ``ai_context.description`` and served per field by the
   * backend `/twin/lenses` + `/twin/bootstrap` endpoints (#2076
   * follow-up: /farm field-hint badges). Absent ⇒ no hint badge.
   */
  description?: string;
  /**
   * English description, projected from ``ai_context.description_en``
   * when the ontology carries it. No field does today — EN tooltips
   * light up per-field as the ontology gains English text; until then
   * the German ``description`` is served for both languages.
   */
  description_en?: string;
  /** HOF-626 — allowed values of an enum-like string field. */
  values?: string[];
  /** HOF-626 — the field accepts an explicit null (clear). */
  nullable?: boolean;
  /** HOF-626 — served only when true: the field may be edited inline. */
  editable?: boolean;
  /** HOF-626 — served only when true: a protected field — editable only by a seat
   *  holding `twin.write.protected` (owner, farm_manager; HOF-696 D20). */
  owner_only?: boolean;
  /** HOF-696 D20 — German display labels of an enum field's values
   *  (`{auto: 'automatisch', …}`); merged into the card's `valueLabels`. */
  value_labels?: Record<string, string>;
}

/** HOF-626 — the 202 body of `PUT /v1/twin/entities/{type}/{id}`. */
export interface EntityEditAccepted {
  status: string;
  entity_type: string;
  entity_id: string;
  update_id?: string;
  field_updates?: Record<string, unknown>;
  message?: string;
}

/** HOF-626 — `GET /v1/twin/entities/{type}/{id}/pending-edits`. */
export interface PendingEditsResponse {
  entity_type: string;
  entity_id: string;
  pending: Record<string, unknown> | null;
  queued: number;
  buffer_depth: number;
  reason: 'buffer_too_deep' | null;
}

/** HOF-626 — a refused edit (object detail, or a string detail as `message`). */
export interface EntityEditRefusal {
  error_kind: string;
  attribute?: string;
  expected_type?: string;
  allowed_values?: string[];
  reason?: string;
}

/** Per-lens vocabulary of the object "Quellen" section (spec §2.2). */
export interface SourcesConfig {
  noun: string;
  master_role?: string | null;
  unassigned_verb: 'assign' | 'merge';
  suggestions: boolean;
}

export interface LenseConfig {
  id: string;
  name: string;
  icon: string;
  /**
   * Short German lens description. Sourced from
   * `config/ontology/lenses.yaml` and served raw by the backend
   * `/twin/lenses` + `/twin/bootstrap` endpoints. Part of the wire shape;
   * currently without a UI consumer (the starting-page tile subtitle it
   * fed was retired with #2108). Optional so the FE degrades gracefully
   * if a lens omits it.
   */
  description?: string;
  entity_type?: string;
  /**
   * The concrete types this lens shows, with their ontology names (FR-017 #3).
   * An abstract lens (`agreements` → `agreement`) lists its subtypes and not
   * itself. Optional: a backend that predates it sends nothing.
   */
  concrete_types?: Array<{ entity_type: string; name: string }>;
  /**
   * Erntejahr axis attribute for this lens (#1934), e.g. `crop_year` on
   * the crop_cycles lens. Sourced raw from `config/ontology/lenses.yaml`.
   * When present, the /farm year navigation reads the distinct values via
   * `GET /twin/entities/{type}/facets?attribute=<year_attribute>` and
   * offers a year filter; absent ⇒ the lens has no year axis. Optional so
   * the lenses contract stays back-compatible.
   */
  year_attribute?: string;
  /**
   * Optional `sources:` block from `lenses.yaml` (FR-018.7, spec §2.2) —
   * the noun / master role / verb used by the Quellen section. Absent keys
   * fall back to `sourcesConfigFor` defaults.
   */
  sources?: Partial<SourcesConfig>;
  feed_display?: FeedDisplay;
  feed_groups?: LenseFeedGroup[];
  item_icons?: LenseItemIcons;
  fields?: LenseField[];
  children: LenseChildConfig[];
}

// --- Entity relations (farm twin-explorer object view) ---

export interface ForwardRelation {
  field: string;
  label: string;
  target_type: string;
  target_id: string;
  display_name: string;
}

export interface RelationSample {
  entity_id: string;
  entity_type: string;
  display_name: string;
}

export interface ReverseRelation {
  source_type: string;
  label: string;
  via_field: string;
  via_label: string;
  count: number;
  sample: RelationSample[];
}

export interface EntityRelations {
  entity_type: string;
  /** German type label from the ontology (headless object views). */
  entity_label?: string;
  entity_id: string;
  forward: ForwardRelation[];
  reverse: ReverseRelation[];
}

export interface LenseCountEntry {
  total: number;
  children: Record<string, number>;
}

export type LenseCounts = Record<string, LenseCountEntry>;

// --- Attribute facet (year navigation axis, #1934) ---

/**
 * One distinct value of a faceted attribute + its live-row count. ``value``
 * is ``null`` for the bucket of rows missing the key OR carrying a JSON-null
 * (e.g. crop_cycles without a ``crop_year``) — that bucket is the "Ohne Jahr"
 * axis entry, never dropped.
 */
export interface EntityFacetItem {
  value: string | null;
  count: number;
  /**
   * #2599 — SUM of the requested ``sumAttribute`` over this group (exact
   * ``numeric`` server-side, JSON on the wire). Present ONLY when the caller
   * asked for one, so ``undefined``/``null`` means "not computed" — never
   * "zero". A caller gating on the measure must keep those apart: a real
   * ``0`` is an empty harvest, an absent one is a server that predates the
   * field (a rolling deploy) and should degrade to ``count``.
   */
  sum?: number | null;
  /**
   * #2599 — rows in this group whose ``sumAttribute`` was not readable as a
   * number (a label, a JSON null, an absent key). Counted, never silently
   * folded into ``sum``.
   */
  sum_skipped?: number;
}

/** One bucket of ``POST /v1/twin/entities/{type}/aggregate`` (#2611). */
export interface EntityAggregateItem {
  /** The grouped attribute values, in ``group_by`` order. ``null`` is a real
   *  bucket (the key was absent or JSON-null on those rows). */
  keys: Record<string, string | null>;
  /** Rows folded into this bucket. */
  count: number;
  /** SUM of ``sum_attribute`` (summed as exact ``numeric`` server-side, then
   *  carried over the wire as JSON); null when none was requested. */
  sum: number | null;
  /** Rows whose ``sum_attribute`` was not readable as a number — counted, never
   *  silently folded into ``sum``. */
  sum_skipped: number;
}

export interface EntityAggregateResponse {
  entity_type: string;
  group_by: string[];
  items: EntityAggregateItem[];
  group_count: number;
  /** Exact uncapped row count of the filtered population. */
  total_rows: number;
  /** The group cap fired — ``items`` is a PARTIAL aggregate. */
  truncated: boolean;
}

/**
 * ``GET /twin/entities/{type}/facets?attribute=`` envelope: distinct values
 * newest-first (server orders ``value DESC NULLS LAST`` — the null bucket
 * last) plus the summed live-row total (the facet universe size).
 */

export interface EntityFacetResponse {
  attribute: string;
  items: EntityFacetItem[];
  total: number;
}

// --- Attribute-transition history + value series (#1913 C) ---

/**
 * One append-only ``twin_attribute_transitions`` row (#1913 B-write),
 * as served by ``GET /twin/entities/{type}/{id}/history``. A transition
 * is recorded only when a JSONB attribute value actually changed on a
 * twin upsert; ``valid_from`` is the write time (UTC) and ``source_id``
 * carries document provenance where the recorder had one.
 *
 * ``old_value``/``new_value`` are the decoded JSON values, but a decoded
 * ``null`` is ambiguous — SQL NULL (attribute appeared / was removed)
 * and an explicit JSON ``null`` value both decode to ``null``. Consumers
 * MUST read the SQL-side ``old_value_absent``/``new_value_absent`` flags
 * to tell "did not exist" from "was explicitly null" (backend codex
 * round-1 P1), never infer it from the decoded value.
 */
export interface AttributeTransition {
  id: number;
  entity_type: string;
  entity_id: string;
  attribute: string;
  old_value: unknown;
  old_value_absent: boolean;
  new_value: unknown;
  new_value_absent: boolean;
  valid_from: string;
  source_id: string | null;
}

export interface EntityHistoryResponse {
  transitions: AttributeTransition[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
}

/**
 * One point of a per-attribute or per-fact value series (#1913 B-read),
 * in the PROVISIONAL ``metric_query``-aligned envelope: ``key`` holds the
 * single time dimension (``valid_from`` for attribute series,
 * ``computed_at`` for fact series), ``measures.value`` is the value the
 * series took at that time. The attribute-series-only fields carry the
 * transition provenance; the fact-series-only fields carry the derived
 * fact's identity + supersession stamp.
 */
export interface EntitySeriesGroup {
  key: Record<string, string | null>;
  measures: { value: unknown };
  count: number;
  // attribute series only
  value_absent?: boolean;
  old_value?: unknown;
  old_value_absent?: boolean;
  source_id?: string | null;
  transition_id?: number;
  // fact series only
  derived_fact_id?: string;
  rule_id?: string | null;
  superseded_at?: string | null;
}

export interface EntitySeriesResponse {
  source: string;
  entity_type: string;
  entity_id: string;
  attribute?: string;
  fact_type?: string;
  grain: string;
  period_applied: { from: string | null; to: string | null };
  dimensions_applied: string[];
  groups: EntitySeriesGroup[];
  total: number;
  truncated: boolean;
  has_data: boolean;
}

/**
 * One entity's state reconstructed at instant ``t`` (#1913 B-read), served by
 * ``GET /twin/entities/{type}/{id}/as-of``: the current row with ``attributes``
 * reverse-patched by every transition after ``t``, plus ``as_of`` (echo) and
 * ``transitions_reverted``. The ``/farm`` object view reconstructs this state
 * CLIENT-side from the fetched transition series (no per-step network); this
 * server call is the agents/API-completeness path and the parity reference the
 * client reverse-replay is tested against.
 */
export interface EntityAsOfResponse {
  entity_id: string;
  entity_type: string;
  attributes: Record<string, unknown>;
  as_of: string;
  transitions_reverted: number;
  [key: string]: unknown;
}

class TwinService {
  // --- Entities ---

  /**
   * Rich-filter list — POST variant of {@link listEntities}. Use this when
   * the GET ``?key=value`` shape can't express what you need; today's
   * motivating case is ``attr_in: {event_id: [...]}`` to fold the N+1
   * event_line fetch into one round trip. Returns the same shape as
   * ``listEntities``.
   */
  async queryEntities(
    entityType: string,
    body: {
      filters?: Record<string, string>;
      attr_in?: Record<string, string[]>;
      filter_missing?: string[];
      /**
       * Get-by-id / FK batch-resolution (farm explorer): filters the
       * typed `entity_id` column UDB-side. Column-level because flat
       * rows don't repeat the pk inside attributes for every type.
       */
      entity_ids?: string[];
      q?: string;
      page?: number;
      limit?: number;
    },
  ): Promise<EntityListResponse> {
    const { data } = await api.post(`/v1/twin/entities/${entityType}/query`, body);
    return data;
  }

  /**
   * FR-017 — every twin item this document is linked to.
   *
   * The direction that did not exist before FR-017: the SPA could always ask
   * "which documents back this entity?" and never "what did this document
   * produce?", which is the question a farmer looking at a scanned
   * certificate actually has.
   */
  async getDocumentLinks(documentId: string): Promise<DocumentLink[]> {
    const { data } = await api.get(
      `/v1/twin/documents/${encodeURIComponent(documentId)}/links`,
    );
    return data?.items ?? [];
  }

  /**
   * FR-017 — every link to one twin item (the entity → documents direction),
   * each with its `link_id`, which the Belege rows do not carry and the
   * entity page's unlink needs.
   */
  async getTargetLinks(targetType: string, targetId: string): Promise<DocumentLink[]> {
    const { data } = await api.get('/v1/twin/links', {
      params: { target_type: targetType, target_id: targetId },
    });
    return data?.items ?? [];
  }

  /**
   * FR-017 #7a — the tenant's link census: live links per type, the documents
   * with no live link ("Ohne Verknüpfung"), and conflicting links.
   */
  async getLinkCensus(): Promise<LinkCensus> {
    const { data } = await api.get('/v1/twin/links/census');
    return data;
  }

  /**
   * Create one link by hand (`created_by = user`). Creating a link the farmer
   * unlinked earlier revives that same row rather than adding a second one —
   * the link id is derived from what the link says, not minted per call.
   */
  async createLink(link: DocumentLinkCreate): Promise<void> {
    await api.post('/v1/twin/links', {
      // No `target_kind`: the backend derives it from `target_type` (an event
      // type is an `event`), which the SPA cannot know on an object page.
      links: [{ payload: {}, ...link }],
    });
  }

  /**
   * End one link. Never a delete — the superseded row is what stops the
   * pipeline recreating it on the next reprocess, so an unlink the farmer
   * performs survives reprocessing. Undoing it means creating the link
   * again, which revives the same row.
   */
  async supersedeLink(linkId: string, reason = 'unlinked'): Promise<void> {
    await api.post(`/v1/twin/links/${encodeURIComponent(linkId)}/supersede`, { reason });
  }

  /**
   * Verknüpfungen for one entity — farm twin-explorer object view.
   * Ontology-driven walk of `references` declarations both directions.
   */
  async getEntityRelations(entityType: string, entityId: string): Promise<EntityRelations> {
    const { data } = await api.get(`/v1/twin/entities/${entityType}/${entityId}/relations`);
    return data;
  }

  // --- Attribute-transition history + value series (#1913 C) ---
  //
  // Reads over the append-only transitions table (#1913 B-read). The
  // backend routes use a ``{entity_id:path}`` converter so slash-bearing
  // ids stay reachable once ASGI percent-decodes ``%2F`` — hence the
  // ``encodeURIComponent`` here mirrors ``getEntitySourceDocs``.

  /**
   * Änderungshistorie for one entity — the append-only attribute
   * transitions recorded on every twin upsert, newest-first
   * (``valid_from DESC, id DESC``). ``attribute`` narrows to one
   * attribute's transitions. Entities predating the recorder simply have
   * an empty history.
   */
  async getEntityHistory(
    entityType: string,
    entityId: string,
    opts?: { attribute?: string; page?: number; limit?: number },
  ): Promise<EntityHistoryResponse> {
    const params: Record<string, string | number> = {};
    if (opts?.attribute) params.attribute = opts.attribute;
    if (opts?.page != null) params.page = opts.page;
    if (opts?.limit != null) params.limit = opts.limit;
    const { data } = await api.get<EntityHistoryResponse>(
      `/v1/twin/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/history`,
      { params },
    );
    return data;
  }

  /**
   * The COMPLETE, newest-first transition series for one entity — pages
   * through ``/history`` (backend caps ``limit`` at 1000) following
   * ``has_more`` until exhausted. The as-of reconstruction needs the FULL
   * series to match the server's unbounded ``get_entity_as_of`` full scan; a
   * single 1000-row page would silently truncate a high-churn entity and
   * break parity (codex round-2 P1).
   *
   * ``PAGE_CAP`` bounds a pathological loop (5000 pages = 5M rows, orders of
   * magnitude past any real single entity's edit count). If it is ever hit
   * while the backend STILL reports ``has_more``, we THROW rather than return a
   * silently-truncated series claiming to be complete (round-3 #3) — the
   * caller surfaces that as an error instead of a wrong reconstruction.
   */
  async getAllEntityTransitions(entityType: string, entityId: string): Promise<AttributeTransition[]> {
    const PAGE_LIMIT = 1000;
    const PAGE_CAP = 5000;
    // ``onCapExceeded: 'throw'`` preserves the round-3 #3 contract: a series
    // truncated at the cap must NEVER masquerade as complete (the caller would
    // reconstruct a wrong as-of state) — surface it as an error instead.
    return fetchAllPages(
      async (page) => {
        const res = await this.getEntityHistory(entityType, entityId, { page, limit: PAGE_LIMIT });
        return { items: res.transitions, hasMore: res.has_more };
      },
      {
        pageCap: PAGE_CAP,
        onCapExceeded: 'throw',
        capMessage:
          `getAllEntityTransitions: exceeded ${PAGE_CAP} pages for ${entityType}/${entityId} with more remaining — ` +
          'refusing to return a truncated series as complete',
      },
    );
  }

  /**
   * Per-attribute or per-fact value series for one entity — exactly ONE
   * of ``attribute`` (points over the transitions table) or ``factType``
   * (a derived fact's superseded generations) must be given. Returns the
   * PROVISIONAL ``metric_query``-aligned envelope with ``groups``
   * ascending by the time dimension. ``fromTime``/``toTime`` apply to the
   * attribute series only.
   */
  async getEntitySeries(
    entityType: string,
    entityId: string,
    opts: { attribute?: string; factType?: string; fromTime?: string; toTime?: string; limit?: number },
  ): Promise<EntitySeriesResponse> {
    const params: Record<string, string | number> = {};
    if (opts.attribute) params.attribute = opts.attribute;
    if (opts.factType) params.fact_type = opts.factType;
    if (opts.fromTime) params.from_time = opts.fromTime;
    if (opts.toTime) params.to_time = opts.toTime;
    if (opts.limit != null) params.limit = opts.limit;
    const { data } = await api.get<EntitySeriesResponse>(
      `/v1/twin/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/series`,
      { params },
    );
    return data;
  }

  /**
   * Entity state reconstructed at instant ``t`` (ISO 8601). Single server call
   * over the transitions table — the object view folds this state client-side
   * from the fetched series instead (no per-step network), so this exists for
   * agents/API completeness and as the parity reference for the client fold.
   */
  async getEntityAsOf(entityType: string, entityId: string, t: string): Promise<EntityAsOfResponse> {
    const { data } = await api.get<EntityAsOfResponse>(
      `/v1/twin/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/as-of`,
      { params: { t } },
    );
    return data;
  }

  async listEntities(
    entityType: string,
    filters?: Record<string, string>,
    pagination?: { page?: number; limit?: number },
    options?: { filterMissing?: string[]; q?: string | null },
  ): Promise<EntityListResponse> {
    const params = new URLSearchParams();
    if (filters) {
      for (const [k, v] of Object.entries(filters)) {
        params.set(k, v);
      }
    }
    if (pagination?.page != null) params.set('page', String(pagination.page));
    if (pagination?.limit != null) params.set('limit', String(pagination.limit));
    // Issue #669 — comma-joined missing-key list. Backend
    // ``twin/entities/{type}`` parses ``_missing`` and forwards to
    // UDB's ``filter_missing`` predicate.
    if (options?.filterMissing && options.filterMissing.length > 0) {
      params.set('_missing', options.filterMissing.join(','));
    }
    // Issue #637 — global header search substring. Backend strips and
    // forwards to UDB ``QueryEntitiesBody.q`` (ILIKE on entity_id +
    // attributes::text). Empty/whitespace strings are skipped so the
    // unfiltered cache key keeps matching the hot path.
    if (options?.q && options.q.trim().length > 0) {
      params.set('q', options.q.trim());
    }
    const qs = params.toString();
    const { data } = await api.get(`/v1/twin/entities/${entityType}${qs ? `?${qs}` : ''}`);
    return data;
  }

  // --- Derived facts ---

  /**
   * Sub-12 of #632 — filtered list of derived facts by ``fact_type``.
   * Used by the Gutschrift-Check FE rewrite to consume
   * ``gutschrift_match_group`` rows directly, replacing the legacy
   * three-way client-side join over the ``event_line.match_*`` bridge.
   */
  async queryDerivedFacts(body: {
    fact_type: string;
    attr_in?: Record<string, string[]>;
    page?: number;
    limit?: number;
  }): Promise<DerivedFactListResponse> {
    const { data } = await api.post('/v1/twin/derived_facts/query', body);
    return data;
  }

  /**
   * #2646 — CATALOG-WIDE ``(day × series)`` price cells, volume-weighted at the
   * source, in ONE call.
   *
   * Replaces the ``queryDerivedFacts({fact_type: 'price_observation'})`` page
   * walk for the Preisspiegel app: that read one row per priced invoice line
   * and truncated at 10 × 1000 against tenants holding 33,000 of them. Cells
   * are O(days × series), so the request that used to lose half a season's
   * history is now a single round trip.
   *
   * ``side``/``items`` narrow it; omitting BOTH is the normal case — the page
   * needs every catalog item resolved before the reader has drilled into one.
   */
  async getPreisspiegelCells(params?: { side?: string; items?: string[] }): Promise<PreisspiegelCellsResponse> {
    const search = new URLSearchParams();
    if (params?.side) search.set('side', params.side);
    for (const item of params?.items ?? []) search.append('item', item);
    const qs = search.toString();
    const { data } = await api.get(`/v1/twin/preisspiegel/cells${qs ? `?${qs}` : ''}`);
    return data;
  }

  /**
   * ``GET /v1/twin/preisspiegel/lines`` — the source lines behind ONE cell
   * (HOF-19 AC 10/11).
   *
   * **On demand only, and the server enforces it**: ``item``, ``seriesId`` and
   * ``date`` are all required there, so there is no request that means "every
   * line in the drawn window". That is not politeness — it is the ~10.000-row
   * ceiling #2646 removed, against a tenant holding 33.000 price observations.
   *
   * Call this when a reader opens a bucket, never on paint.
   */
  async getPreisspiegelLines(params: {
    item: string;
    seriesId: string;
    date: string;
    bucket?: 'day' | 'month';
  }): Promise<PreisspiegelLinesResponse> {
    const search = new URLSearchParams({ item: params.item, series_id: params.seriesId, date: params.date });
    if (params.bucket) search.set('bucket', params.bucket);
    const { data } = await api.get(`/v1/twin/preisspiegel/lines?${search.toString()}`);
    return data;
  }

  /** HOF-19 Phase B — the retail observations behind ONE benchmark cell. */
  async getPreisspiegelBenchmarkLines(params: {
    item: string;
    seriesId: string;
    date: string;
    bucket?: 'day' | 'week' | 'month';
  }): Promise<PreisspiegelBenchmarkLinesResponse> {
    const search = new URLSearchParams({ item: params.item, series_id: params.seriesId, date: params.date });
    if (params.bucket) search.set('bucket', params.bucket);
    const { data } = await api.get(`/v1/twin/preisspiegel/benchmark-lines?${search.toString()}`);
    return data;
  }

  /**
   * ``GET /v1/twin/preisspiegel/index`` — one row per ``(catalog_item_id,
   * side)`` (#2694). The read the page SHELL renders on.
   *
   * ``/cells`` is catalog-wide but still O(days × series) — ~6.700 cells on WF,
   * and a ``?kat=saatgut`` deep link pays for every Erdbeeren cell before it can
   * draw seven Saatgut tiles. This is bounded by the CATALOG instead (one row per
   * item × side), because the server drops the time key from the group: a few dozen
   * rows, one round trip. Everything else the shell needs — names, units, price
   * bases, the Warengruppe→Kategorie tree — is compiled into the bundle from
   * ``config/catalog/preisspiegel@v1.yaml`` (#2771).
   */
  async getPreisspiegelIndex(params?: { side?: string; items?: string[] }): Promise<PreisspiegelIndexResponse> {
    const search = new URLSearchParams();
    if (params?.side) search.set('side', params.side);
    for (const item of params?.items ?? []) search.append('item', item);
    const qs = search.toString();
    const { data } = await api.get(`/v1/twin/preisspiegel/index${qs ? `?${qs}` : ''}`);
    return data;
  }

  /**
   * ``GET /v1/twin/preisspiegel/tiles`` — ``/index`` + ``/cells`` in one hop
   * (HOF-271). See {@link PreisspiegelTilesResponse}.
   *
   * ``kat`` scopes the cell half to a Kategorie; ``items`` scopes it explicitly
   * and wins, which is the drilled-in chart's shape (one item, not its fifteen
   * siblings — #2694 stage 3). Sending NEITHER is the cold visit: the server
   * resolves first-enabled and says which it chose.
   *
   * **No ``If-None-Match`` is set here, deliberately.** The response carries
   * ``ETag`` + ``Cache-Control: private, no-cache``, so the BROWSER revalidates
   * and serves its own cached body on the 304. Managing the validator in JS would
   * mean a second cache beside the HTTP one, with its own eviction and its own
   * ways of going stale — and the backend would still have to answer both.
   */
  async getPreisspiegelTiles(params?: {
    kat?: string | null;
    items?: readonly string[];
    side?: string;
    bucket?: 'day' | 'month';
  }): Promise<PreisspiegelTilesResponse> {
    const search = new URLSearchParams();
    if (params?.kat) search.set('kat', params.kat);
    for (const item of params?.items ?? []) search.append('item', item);
    if (params?.side) search.set('side', params.side);
    if (params?.bucket) search.set('bucket', params.bucket);
    // HOF-270 — a CAPABILITY assertion, not a preference: this bundle can draw a tile
    // from the materialised `summaries` block, so the server may withhold the own-price
    // cells behind it. A cached pre-HOF-270 bundle sends no such flag and is served the
    // full cell set it has always been served, which is what keeps a rolling deploy from
    // rendering "Keine Daten" over good prices.
    search.set('summaries', '1');
    const qs = search.toString();
    const { data } = await api.get(`/v1/twin/preisspiegel/tiles${qs ? `?${qs}` : ''}`);
    return data;
  }

  /**
   * HOF-377 — the grid's FIRST paint: titles, prices and *Stand*, no curve.
   *
   * A separate ROUTE rather than a flag on `/tiles` (spec Assumption 6), and the
   * reason is the server's pin: "issues no benchmark call" is far easier to prove
   * of a handler that has no benchmark leg than of a branch inside one that still
   * carries the index leg and the cold-visit fallback. It also gives the deploy
   * window a clean capability boundary — a browser holding a cached pre-change
   * bundle never calls a route it does not know, and is served by `/tiles` exactly
   * as today for as long as that bundle lives (AC 23).
   */
  async getPreisspiegelTileHeadlines(params?: {
    kat?: string | null;
    items?: readonly string[];
    side?: string;
  }): Promise<PreisspiegelTileHeadlinesResponse> {
    const search = new URLSearchParams();
    if (params?.kat) search.set('kat', params.kat);
    for (const item of params?.items ?? []) search.append('item', item);
    if (params?.side) search.set('side', params.side);
    const qs = search.toString();
    const { data } = await api.get(`/v1/twin/preisspiegel/tiles/headlines${qs ? `?${qs}` : ''}`);
    return data;
  }

  /**
   * HOF-377 — the 120-day sparkline points for the resolved scope.
   *
   * ONE batched request rather than one per tile (spec Assumption 5): the points
   * come from the same rows, so per-tile would issue up to ~40 requests each
   * re-reading one table, for a Kategorie the catalog already bounds well under
   * that. Fired immediately after the headline response resolves — the requirement
   * is that first paint never waits for it, not that it is lazy about starting.
   */
  async getPreisspiegelTileHistories(params?: {
    kat?: string | null;
    items?: readonly string[];
    side?: string;
  }): Promise<PreisspiegelTileHistoriesResponse> {
    const search = new URLSearchParams();
    if (params?.kat) search.set('kat', params.kat);
    for (const item of params?.items ?? []) search.append('item', item);
    if (params?.side) search.set('side', params.side);
    const qs = search.toString();
    const { data } = await api.get(`/v1/twin/preisspiegel/tiles/histories${qs ? `?${qs}` : ''}`);
    return data;
  }

  // --- Events ---

  async listEvents(filters?: EventFilters): Promise<EventListResponse> {
    const params = new URLSearchParams();
    if (filters?.severity) params.set('severity', filters.severity);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.event_type) params.set('event_type', filters.event_type);
    if (filters?.category) params.set('category', filters.category);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));

    const { data } = await api.get(`/v1/twin/events?${params.toString()}`);
    return data;
  }

  async acknowledgeEvent(eventId: string): Promise<TwinEvent> {
    const { data } = await api.put(`/v1/twin/events/${eventId}/acknowledge`);
    return data;
  }

  async dismissEvent(eventId: string): Promise<TwinEvent> {
    const { data } = await api.put(`/v1/twin/events/${eventId}/dismiss`);
    return data;
  }

  async resolveEvent(eventId: string): Promise<TwinEvent> {
    const { data } = await api.put(`/v1/twin/events/${eventId}/resolve`);
    return data;
  }

  // --- Prüfstatus ---

  async getPruefstatus(): Promise<Pruefstatus> {
    const { data } = await api.get('/v1/twin/pruefstatus');
    return data;
  }

  async getPruefstatusArea(areaId: string): Promise<PruefstatusArea & { events: TwinEvent[] }> {
    const { data } = await api.get(`/v1/twin/pruefstatus/${areaId}`);
    return data;
  }

  // --- Provenance registry (#3385) ---

  /**
   * The deploy-wide source-system registry behind the EIGENSCHAFTEN source
   * badges — ``config/source-systems.yaml``, served by the backend.
   *
   * Not tenant-scoped and effectively immutable for the life of a deployed
   * image (the loader is ``@lru_cache``'d and the file is baked in), which is
   * why ``useSourceSystems`` fetches it once with ``staleTime: Infinity``.
   *
   * A 500 (registry failed to load) propagates as a rejection — the caller
   * degrades to the bundled defaults rather than blanking every badge.
   *
   * A non-array body degrades to ``[]`` rather than being passed through, so
   * the shape the caller must reason about is exactly one: an array, possibly
   * empty. ``useSourceSystems`` then treats empty as "no usable answer".
   */
  async getSourceSystems(): Promise<SourceSystemEntry[]> {
    const { data } = await api.get<{ source_systems?: SourceSystemEntry[] }>(
      '/v1/twin/provenance/source-systems',
    );
    const list = data?.source_systems;
    return Array.isArray(list) ? list : [];
  }

  // --- Entity Documents (linked source documents via facts) ---

  async getEntitySourceDocs(
    entityType: string,
    entityId: string,
  ): Promise<EntitySourceDoc[]> {
    const { data } = await api.get<EntitySourceDoc[]>(
      `/v1/twin/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/documents`,
    );
    return data ?? [];
  }

  async getEntityDocumentsBulk(
    entityType: string,
    entityIds: string[],
  ): Promise<Record<string, EntitySourceDoc[]>> {
    if (entityIds.length === 0) return {};
    const { data } = await api.get<{
      entity_type: string;
      documents_by_entity: Record<string, EntitySourceDoc[]>;
    }>(`/v1/twin/entities/documents`, {
      params: { entity_type: entityType, entity_ids: entityIds.join(',') },
    });
    return data.documents_by_entity ?? {};
  }

  // --- Entity Events (reverse-reference lookup) ---
  //
  // The single-entity ``getEntityEvents`` was removed when the FarmPage
  // hoisted to the bulk variant — no FE call site referenced it. The
  // backend route stays in place; re-add the 3-line method here if a
  // future caller needs it.

  async getEntityEventsBulk(
    entityType: string,
    entityIds: string[],
  ): Promise<Record<string, EntityEvent[]>> {
    if (entityIds.length === 0) return {};
    const { data } = await api.get<{
      entity_type: string;
      events_by_entity: Record<string, EntityEvent[]>;
    }>(`/v1/twin/entities/events`, {
      params: { entity_type: entityType, entity_ids: entityIds.join(',') },
    });
    return data.events_by_entity ?? {};
  }

  // --- Entity Edit ---

  async editEntity(
    entityType: string,
    entityId: string,
    fieldUpdates: Record<string, unknown>,
  ): Promise<EntityEditAccepted> {
    const { data } = await api.put<EntityEditAccepted>(`/v1/twin/entities/${entityType}/${entityId}`, {
      field_updates: fieldUpdates,
    });
    return data;
  }

  async getPendingEdits(entityType: string, entityId: string): Promise<PendingEditsResponse> {
    const { data } = await api.get<PendingEditsResponse>(`/v1/twin/entities/${entityType}/${entityId}/pending-edits`);
    return data;
  }

  /** HOF-626 — the PUT's two refusal shapes (422/404 object `detail`, 400/403 string `detail`). */
  readEditRefusal(err: unknown): EntityEditRefusal | null {
    const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    if (detail == null) return null;
    if (typeof detail === 'string') return { error_kind: 'message', reason: detail };
    if (typeof detail === 'object' && typeof (detail as { error_kind?: unknown }).error_kind === 'string') {
      return detail as EntityEditRefusal;
    }
    return null;
  }

  // --- Match-group line link (gutschrift-check, #1051) ---

  /**
   * Link or unlink a Lieferschein↔Gutschrift line pair from the
   * gutschrift-check canvas. Mutates ``gutschrift_match_group.line_pairs``
   * via the user-scoped backend route (synchronous UDB upsert so the
   * optimistic edge gets a deterministic ack).
   *
   * Both ids are ``event_line`` entity ids of the shape
   * ``f"{event_id}:{idx}"``. ``match_status`` defaults to ``matched``;
   * the canvas drag tool never emits ``quantity_mismatch`` (auto-
   * detector territory). Errors surface as ``409 third_party_match`` /
   * ``409 stale_unlink`` / ``404 match_group_not_found`` from the
   * backend — caller is expected to clear the optimistic edge on
   * failure and toast the ``error_kind``.
   */
  async linkEventLines(args: {
    delivery_line_id: string;
    settlement_line_id: string;
    action: 'link' | 'unlink';
    match_status?: 'matched' | 'quantity_mismatch';
  }): Promise<{
    applied: boolean;
    action: 'link' | 'unlink';
    delivery_line_id: string;
    settlement_line_id: string;
    match_status: string;
    group_id: string;
    derived_fact_id: string;
  }> {
    const { data } = await api.post(
      '/v1/twin/match-groups/line-link',
      args,
    );
    return data;
  }

  // --- Match-group DOCUMENT link (gutschrift-check, HOF-219) ---

  /**
   * Sever (or restore) a Gutschrift↔Lieferschein DOCUMENT edge.
   *
   * One layer above ``linkEventLines``: that one says "these two LINES describe
   * the same goods", this one says "these two DOCUMENTS do not belong together"
   * — the thing an operator looking at a Lieferschein grouped onto the wrong
   * Gutschrift is actually asserting, and which the line layer structurally
   * cannot express.
   *
   * Both ids are EVENT entity ids (``settlement_event`` / ``delivery_event``),
   * not the ``"<event_id>:<idx>"`` line ids the sibling call takes.
   *
   * The write is durable: the backend records the exclusion on the Gutschrift
   * and publishes ``twin_recalculate``, so the group split (or non-split)
   * arrives asynchronously — the synchronous part is the edge and the spanning
   * line pairs disappearing from the fact. ``applied: false`` means the request
   * was a historical no-op (already severed / already linked), which callers
   * must not count as a mutation.
   *
   * Errors: ``404 event_not_found`` / ``404 match_group_not_found`` /
   * ``409 stale_unlink`` / ``409 concurrent_document_link`` /
   * ``502 unlinked_delivery_write_failed``, all mapped for the operator by
   * ``mapLineLinkErrorToToastMessage``.
   */
  async linkDocuments(args: {
    settlement_event_id: string;
    delivery_event_id: string;
    action: 'link' | 'unlink';
  }): Promise<{
    applied: boolean;
    action: 'link' | 'unlink';
    settlement_event_id: string;
    delivery_event_id: string;
    link_kind: string | null;
    group_id: string;
    derived_fact_id: string;
  }> {
    const { data } = await api.post('/v1/twin/match-groups/document-link', args);
    return data;
  }

  // --- Liquiditätsplanung (#1315) ---

  /** Create/edit/move one planned transaction (id present ⇒ edit/move). */
  async upsertLiquiditaetPlannedTransaction(body: {
    derived_fact_id?: string;
    direction: 'income' | 'expense';
    amount_eur: number;
    qty?: number;
    unit_price_eur?: number;
    value_date: string;
    period_ym: string;
    label: string;
    ledger_category?: string; // one of config/ontology/ledger_category ids; server enum-validates
    notes?: string;
    qty_meta?: { ha?: number; t?: number; eur_per_t?: number };
    account_id?: string; // #2211: owning bank account (multi-account); omitted key on single-account
    counterparty_id?: string; // #2678 c: picked ``organization`` id; omitted key when unpicked
  }): Promise<{ derived_fact_id: string; fact_value: Record<string, unknown> }> {
    const { data } = await api.post('/v1/twin/liquiditaet/planned-transactions', body);
    return data;
  }

  // --- Transaction ↔ twin link (#1624-D) ---
  //
  // Consumes the #1623 ``liquiditaet_twin_link`` contract through the
  // user-facing backend endpoints. Two flows: a transaction → ``crop_cycle``
  // harvest link (Deckungsbeitrag join) and a Bodenprobe document → lab-payment
  // transaction link. ``manage`` is idempotent (deterministic link id);
  // ``unlink`` soft-deletes; reads are live-only.

  /** Link or unlink one transaction ↔ twin item (document or entity). */
  async manageLiquiditaetLink(body: {
    action: 'link' | 'unlink';
    transaction_id: string;
    target_kind: 'document' | 'entity';
    document_id?: string;
    target_entity_type?: string;
    target_entity_id?: string;
    link_role?: string;
    note?: string;
  }): Promise<LiquiditaetLinkResult> {
    const { data } = await api.post('/v1/twin/liquiditaet/links', body);
    return data;
  }

  /** Read the live links FOR a transaction or FOR a twin item. */
  async listLiquiditaetLinks(
    params:
      | { mode: 'transaction'; transaction_id: string; limit?: number }
      | {
          mode: 'twin_item';
          target_kind: 'document' | 'entity';
          document_id?: string;
          target_entity_type?: string;
          target_entity_id?: string;
          limit?: number;
        },
  ): Promise<LiquiditaetLinkListResponse> {
    const { data } = await api.get('/v1/twin/liquiditaet/links', { params });
    return data;
  }

  // --- Stats ---

  async getStats(): Promise<TwinStats> {
    const { data } = await api.get('/v1/twin/stats');
    return data;
  }

  // --- Lenses ---

  async getLenses(): Promise<LenseConfig[]> {
    const { data } = await api.get('/v1/twin/lenses');
    return data;
  }

  async getLenseCounts(q?: string | null): Promise<LenseCounts> {
    // Issue #637 — pass the global header search through so sidebar
    // tallies match the search-filtered entity feed. Empty/whitespace
    // strings are skipped so search-less callers keep hitting the
    // same Redis cache key the bootstrap path uses.
    const qs = q && q.trim().length > 0 ? `?q=${encodeURIComponent(q.trim())}` : '';
    const { data } = await api.get(`/v1/twin/lenses/counts${qs}`);
    return data;
  }

  /**
   * Phase 07-6 kill-list #1 (2026-05-01): single bootstrap call that
   * collapses the FarmPage cold-load waterfall (lenses → counts →
   * entities) into one round trip. The backend fans out the reads
   * via ``asyncio.gather`` so the worst case is ``max()`` not
   * ``sum()``. Pass ``lense`` (the URL ``lense=`` slug) to include
   * the entity list in the same payload — the backend resolves
   * ``lense → entity_type`` server-side so the FE doesn't need to
   * fetch lenses first to know which entity_type to ask for.
   */
  async getFarmBootstrap(
    lense?: string,
    pagination?: { page?: number; limit?: number },
  ): Promise<{
    lenses: LenseConfig[];
    counts: LenseCounts;
    entities: EntityListResponse | null;
    entity_type: string | null;
    lense: string | null;
  }> {
    const params = new URLSearchParams();
    if (lense) params.set('lense', lense);
    if (pagination?.page != null) params.set('page', String(pagination.page));
    if (pagination?.limit != null) params.set('limit', String(pagination.limit));
    const qs = params.toString();
    const { data } = await api.get(`/v1/twin/bootstrap${qs ? `?${qs}` : ''}`);
    return data;
  }

  // --- Entity attribute facet (year navigation axis, #1934) ---
  //
  // #1934's own twinService section — deliberately apart from the #1973
  // transition-paging helpers above (``getAllEntityTransitions``) so the two
  // sibling 28.5 PRs never touched the same block.

  /**
   * Distinct values + per-value counts of one attribute (#1934 year axis).
   * Backs the /farm Erntejahr control: ``getEntityFacets('crop_cycle',
   * 'crop_year')`` → ``{attribute, items: [{value, count}], total}`` with the
   * NULL bucket (rows missing the key) included as ``{value: null, …}``,
   * ordered newest-value-first (server ``DESC NULLS LAST``). Generic over any
   * ontology attribute — the endpoint whitelists the charset, so ``attribute``
   * can only ever name a JSONB key. ``encodeURIComponent`` on the type mirrors
   * ``getEntityHistory`` (slash-bearing ids stay reachable).
   */
  async getEntityFacets(entityType: string, attribute: string): Promise<EntityFacetResponse> {
    const { data } = await api.get<EntityFacetResponse>(
      `/v1/twin/entities/${encodeURIComponent(entityType)}/facets`,
      { params: { attribute } },
    );
    return data;
  }

  /**
   * Filtered distinct-value facet (#2419) — POST sibling of
   * {@link getEntityFacets}. Adds a JSONB containment ``filters`` (e.g.
   * ``{type: 'pick'}``) and an inclusive ``dateRange`` window on a JSONB date
   * attribute, so the Ernteprognose lens can count crop_cycles with real pick
   * data inside a Wirtschaftsjahr window in ONE indexed GROUP BY (never N
   * per-group pick fetches). Returns the same ``{attribute, items, total}``
   * envelope; ``items[].value`` is the grouped ``attribute`` value (the
   * crop_cycle_id), ``count`` the in-window pick count.
   */
  async getEntityFacetsFiltered(
    entityType: string,
    body: {
      attribute: string;
      filters?: Record<string, string>;
      dateRange?: { attribute: string; from?: string; to?: string };
      /**
       * #2599 — numeric JSONB attribute to SUM per group. Each item then
       * carries ``sum`` + ``sum_skipped``, letting a caller gate on the
       * MEASURE rather than on row existence. Omit it and the request and
       * response are byte-identical to the pre-#2599 shape.
       */
      sumAttribute?: string;
    },
  ): Promise<EntityFacetResponse> {
    const payload: Record<string, unknown> = { attribute: body.attribute };
    if (body.filters) payload.filters = body.filters;
    if (body.dateRange) {
      payload.date_range = {
        attribute: body.dateRange.attribute,
        from: body.dateRange.from,
        to: body.dateRange.to,
      };
    }
    if (body.sumAttribute) payload.sum_attribute = body.sumAttribute;
    const { data } = await api.post<EntityFacetResponse>(
      `/v1/twin/entities/${encodeURIComponent(entityType)}/facets`,
      payload,
    );
    return data;
  }

  /**
   * Grouped COUNT/SUM over JSONB entity attributes (#2611) — the multi-key,
   * SUM-capable sibling of ``getEntityFacetsFiltered``.
   *
   * Lets a feed read a per-bucket aggregate instead of paging every row into
   * the browser. The Liquiditätsplanung balance feed used to walk ~19k
   * ``cash_flow`` rows behind a 10-page guard and silently stop there; one
   * ``account_id x value_date x status x planned_ref`` aggregate returns ~1.8k
   * buckets and nothing is dropped.
   *
   * ``truncated`` is the fail-loud group-cap signal — never render a
   * ``truncated`` aggregate as a complete picture.
   */
  async aggregateEntities(
    entityType: string,
    body: {
      groupBy: string[];
      sumAttribute?: string;
      filters?: Record<string, unknown>;
      attrIn?: Record<string, string[]>;
      attrNotIn?: Record<string, string[]>;
      filterMissing?: string[];
      maxGroups?: number;
    },
  ): Promise<EntityAggregateResponse> {
    const payload: Record<string, unknown> = { group_by: body.groupBy };
    if (body.sumAttribute) payload.sum_attribute = body.sumAttribute;
    if (body.filters) payload.filters = body.filters;
    if (body.attrIn) payload.attr_in = body.attrIn;
    if (body.attrNotIn) payload.attr_not_in = body.attrNotIn;
    if (body.filterMissing) payload.filter_missing = body.filterMissing;
    if (body.maxGroups) payload.max_groups = body.maxGroups;
    const { data } = await api.post<EntityAggregateResponse>(
      `/v1/twin/entities/${encodeURIComponent(entityType)}/aggregate`,
      payload,
    );
    return data;
  }
}

export const twinService = new TwinService();
